import { useCallback } from 'react';
import { useRouter } from '@tanstack/react-router';
import { useAuthStore } from '@/shared/store/auth.store';
import {
  useGuiaInteractivaStatusQuery,
  useMarcarTourVistoMutation,
} from '@/features/guia-interactiva/queries/guia-interactiva.queries';
import { TOURS } from '@/features/guia-interactiva/tours';
import { useDriver } from './use-driver';
import type { TourDefinicion } from '@/features/guia-interactiva/types/guia-interactiva.types';

interface LanzarOpciones {
  bloqueante: boolean;
  /**
   * Si `true` (default), cerrar el tour con X o Esc también lo marca como
   * visto — el usuario no volverá a verlo automáticamente, pero puede
   * relanzarlo desde el botón ? o el índice.
   *
   * Si `false`, cerrar sin llegar a Finalizar NO persiste y el tour se
   * vuelve a disparar automáticamente la próxima vez que el usuario entra
   * al menú (comportamiento "insistente").
   *
   * Para cambiar la política por defecto, ajustar el default en
   * `DEFAULT_MARCAR_VISTO_AL_CERRAR` más abajo.
   */
  marcarVistoAlCerrar?: boolean;
}

// Política por defecto: respetar la decisión del usuario (estándar de
// industria). Cambiá este valor a `false` si preferís el comportamiento
// insistente para todos los tours.
const DEFAULT_MARCAR_VISTO_AL_CERRAR = true;

/**
 * Hook raíz del contexto de guía interactiva. Se monta una única vez en
 * `GuiaInteractivaProvider`.
 *
 * Mantiene la ÚNICA instancia de `useDriver` de toda la app. driver.js usa
 * estado global (issues #464 y #504 del repo oficial) y no soporta múltiples
 * instancias; tanto los tours auto-disparados por pantalla como los
 * manuales del índice pasan por esta misma instancia.
 */
export function useGuiaInteractiva() {
  const router = useRouter();
  const permisos = useAuthStore(state => state.user?.permisos);
  const { data: status, isLoading } = useGuiaInteractivaStatusQuery();
  // React Query retorna un objeto NUEVO cada render, pero `mutate` sí es
  // referencialmente estable. Destructurar evita que los consumidores del
  // context reciban nuevas refs en cada render y re-ejecuten efectos.
  const { mutate: marcarTourVisto } = useMarcarTourVistoMutation();
  const { ejecutar, cerrar } = useDriver();

  const lanzarTour = useCallback(
    async (tour: TourDefinicion, opciones: LanzarOpciones) => {
      const setPermisos = new Set<string>(permisos ?? []);
      if (tour.permisoMenu && !setPermisos.has(tour.permisoMenu)) return;

      const pasos = tour.pasos.filter(
        p => !p.permisoRequerido || setPermisos.has(p.permisoRequerido)
      );
      if (pasos.length === 0) return;

      // Si el tour declara `ruta` fija y el usuario no está en ella, navegamos.
      // Para tours que viven en rutas paramétricas (ej. editor de tecnología en
      // `/admin/recet/tecnologias/$id`) `ruta` es undefined y no se navega —
      // el tour corre en la pantalla donde se disparó.
      if (tour.ruta && window.location.pathname !== tour.ruta) {
        await router.navigate({ to: tour.ruta });
      }

      const marcarVistoAlCerrar = opciones.marcarVistoAlCerrar ?? DEFAULT_MARCAR_VISTO_AL_CERRAR;

      await ejecutar({
        pasos,
        permitirCerrar: !opciones.bloqueante,
        onCompletar: () => marcarTourVisto(tour.codigo),
        onCerrar: marcarVistoAlCerrar ? () => marcarTourVisto(tour.codigo) : () => {},
      });
    },
    [permisos, router, ejecutar, marcarTourVisto]
  );

  const lanzarTourPorCodigo = useCallback(
    async (codigo: string, opciones: LanzarOpciones) => {
      const tour = TOURS[codigo];
      if (!tour) return;
      await lanzarTour(tour, opciones);
    },
    [lanzarTour]
  );

  return {
    status,
    isLoading,
    lanzarTour,
    lanzarTourPorCodigo,
    cerrar,
    marcarTourVisto,
  };
}
